import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { colorOverrides as colors } from "theme/colorOverrides";
import { DocEntry, docPath } from "../docsManifest";
import { DOCS_ROW_INSET } from "../docsStyles";

const CHEVRON_WIDTH = 24;

const RowButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "depth",
})<{ depth: number; component: typeof Link; to: string }>(
  ({ theme, depth }) => ({
    // Leaf text lines up with a parent's chevron; a child's text lines up with its parent's text.
    paddingLeft: DOCS_ROW_INSET + depth * CHEVRON_WIDTH,
    paddingRight: theme.spacing(3),
    // Phone rows keep a ~44px tap target; desktop tightens up.
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderRadius: Number(theme.shape.borderRadius) * 2,
    color: colors.gray04,
    [theme.breakpoints.up("md")]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    "&:hover": { backgroundColor: colors.brand14 },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: colors.brand13,
      color: colors.brand07,
    },
  })
);

export const DOCS_TREE_LIST_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
} as const;

interface DocsTreeItemProps {
  entry: DocEntry;
  depth: number;
  activeSlug: string;
  expanded: Set<string>;
  /** While searching, every matching branch is shown open. */
  searching: boolean;
  onToggle: (slug: string) => void;
  onNavigate?: () => void;
}

export const DocsTreeItem = ({
  entry,
  depth,
  activeSlug,
  expanded,
  searching,
  onToggle,
  onNavigate,
}: DocsTreeItemProps) => {
  const hasChildren = !!entry.children?.length;
  const open = searching || expanded.has(entry.slug);
  const selected = entry.slug === activeSlug;

  return (
    <Box>
      <RowButton
        component={Link}
        to={docPath(entry.slug)}
        depth={depth}
        selected={selected}
        onClick={onNavigate}
      >
        {hasChildren && (
          <IconButton
            size="small"
            aria-label={`${open ? "Collapse" : "Expand"} ${entry.title}`}
            onClick={(event) => {
              // The row itself navigates; the chevron only expands.
              event.preventDefault();
              event.stopPropagation();
              onToggle(entry.slug);
            }}
            sx={{
              width: CHEVRON_WIDTH,
              height: CHEVRON_WIDTH,
              ml: "-2px",
              mr: "2px",
              color: "inherit",
            }}
          >
            {open ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        )}
        <ListItemText
          primary={entry.title}
          primaryTypographyProps={{
            fontSize: 13,
            lineHeight: 1.35,
            fontWeight: selected || (hasChildren && open) ? 500 : 400,
            color: hasChildren && open && !selected ? colors.gray02 : "inherit",
          }}
          sx={{ my: 0 }}
        />
      </RowButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ ...DOCS_TREE_LIST_SX, mt: 0.5 }}>
            {entry.children!.map((child) => (
              <DocsTreeItem
                key={child.slug}
                entry={child}
                depth={depth + 1}
                activeSlug={activeSlug}
                expanded={expanded}
                searching={searching}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};
